/**
 * Single Sprite Factory
 *
 * Factory function to create PIXI.Sprite instances for non-tiling parallax layers.
 * Single sprites are scaled to fit within the bleed bounds using "contain" fit (maintaining aspect ratio).
 *
 * @example
 * ```typescript
 * const sprite = createSingleSprite(
 *   config,
 *   texture,
 *   { minX: -250, maxX: 1250, minY: -200, maxY: 1000, width: 1500, height: 1200 }
 * );
 * ```
 */

import * as PIXI from 'pixi.js';
import type { ParallaxLayerConfig } from '../../types/terrain';
import type { BleedBounds } from './bleed-calculator';

/**
 * Create a PIXI.Sprite from parallax layer configuration
 *
 * The Sprite is scaled to fit within the bleed bounds using "contain" fit
 * (similar to CSS background-size: contain). This means the image will be
 * scaled to fit entirely within the bounds while maintaining aspect ratio,
 * ensuring the full image is visible.
 *
 * The sprite is centered on the bleed bounds.
 *
 * @param config - Parallax layer configuration
 * @param texture - PIXI texture to use for the sprite
 * @param bounds - Bleed bounds defining the sprite's coverage area
 * @returns Configured PIXI.Sprite ready for parallax updates
 *
 * @example
 * ```typescript
 * const nearLayer = createSingleSprite(
 *   {
 *     textureUrl: '/assets/battlefield-floor.jpg',
 *     parallaxFactor: 0.5,
 *     scale: 1.0,
 *     alpha: 1.0,
 *   },
 *   nearTexture,
 *   bleedBounds
 * );
 * ```
 */
export function createSingleSprite(
  config: ParallaxLayerConfig,
  texture: PIXI.Texture,
  bounds: BleedBounds
): PIXI.Sprite {
  // Create sprite with the provided texture
  const sprite = new PIXI.Sprite(texture);

  // Set alpha from config
  sprite.alpha = config.alpha;

  // Calculate scale to contain bounds (like CSS background-size: contain)
  // This maintains aspect ratio while ensuring the entire image fits within bounds
  const textureAspect = texture.width / texture.height;
  const boundsAspect = bounds.width / bounds.height;

  let scale: number;
  if (textureAspect > boundsAspect) {
    // Texture is wider - scale based on width to fit
    scale = bounds.width / texture.width;
  } else {
    // Texture is taller or equal - scale based on height to fit
    scale = bounds.height / texture.height;
  }

  // Apply config scale multiplier
  scale *= config.scale;

  // Set sprite scale
  sprite.scale.set(scale);

  // Store the base scale for zoom calculations
  // This is used by parallax-updater to maintain correct scaling during camera zoom
  (sprite as any).__baseScale = scale;

  // Center the sprite on the bounds
  sprite.anchor.set(0.5, 0.5);

  // Position at center of bounds, then apply pixel offsets from theme config
  const centerX = bounds.minX + bounds.width / 2;
  const centerY = bounds.minY + bounds.height / 2;
  sprite.x = centerX + (config.offsetX ?? 0);
  sprite.y = centerY + (config.offsetY ?? 0);

  return sprite;
}
